/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  switchMap,
  tap
} from 'rxjs';
import { BaseComponent } from '~backoffice/app/~shared/abstract-components/base.component';
import { ListFilter, SelectData } from '~core/models/shared.models';

@Component({
  selector: 'search-bar-input',
  standalone: false,
  templateUrl: './search-bar-input.component.html',
  styleUrls: ['./search-bar-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchBarInputComponent
  extends BaseComponent
  implements AfterViewInit
{
  search = output<string | null>();
  searchInput = viewChild<ElementRef<HTMLInputElement>>('input');
  placeholder = input<string>('');
  defaultValue = input<string | null>(null);
  autoCompleteFn = input<
    | Pick<ListFilter, 'autocompleteValues$'>['autocompleteValues$']
    | null
    | undefined
  >(undefined);

  autoCompleteKeyToEmit = input<'name' | 'value'>('name');

  searchButton = input<boolean>(true);
  searchValue = signal<string | null>(null);

  searchForm = new FormGroup({
    search: new FormControl<string | SelectData | null>(
      this.defaultValue() ?? null
    )
  });

  autoCompleteValues = signal<SelectData[]>([]);

  constructor() {
    super();
    this.searchForm.controls.search.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(value => {
          const autoCompleteFn = this.autoCompleteFn();
          if (autoCompleteFn) return;
          this.searchValue.set(typeof value == 'string' ? value : value?.value);
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.listenToSearchChanges();
  }

  listenToSearchChanges(): void {
    this.searchForm
      .get('search')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this._destroyRef),
        distinctUntilChanged(),
        debounceTime(600),
        switchMap(search => {
          const query =
            typeof search === 'string'
              ? search
              : search?.[this.autoCompleteKeyToEmit()];
          const autoCompleteFn = this.autoCompleteFn();
          if (!autoCompleteFn || !query) return EMPTY;
          return autoCompleteFn(query).pipe(
            tap({ next: items => this.autoCompleteValues.set(items) })
          );
        })
      )
      .subscribe();
  }

  onCleanSearch(): void {
    this.searchForm.patchValue({ search: null });
    this.searchInput()?.nativeElement.focus();
    this.searchValue.set(null);
    this.search.emit(null);
  }

  onSubmit(): void {
    const autoCompleteFn = this.autoCompleteFn();
    if (autoCompleteFn) return;
    const search = this.searchForm.controls.search.value;
    const query = typeof search === 'string' ? search : search?.value;
    this.searchValue.set(query ?? null);
    this.search.emit(this.searchValue()?.trim() || null);
  }

  selectOptionDisplayFn(option: SelectData): string {
    return option && option.name ? option.name : '';
  }

  emitSearch(value: string | null | undefined): void {
    this.search.emit(value?.trim() ?? null);
  }
}
